import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isMobile;
};
export const SectionReveal = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => {
  return <motion.section id={id} initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className={`py-20 md:py-32 relative z-10 ${className}`} data-id="element-208">
      {children}
    </motion.section>;
};
export const GoldButton = ({
  children,
  onClick,
  href,
  target,
  rel,
  className = ''
}: {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
}) => {
  const baseClasses = 'inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-gold to-gold-glow text-celestial-900 font-bold uppercase tracking-wider rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] transition-all duration-300 transform hover:-translate-y-1 text-sm md:text-base';
  if (href) {
    return <a href={href} target={target} rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)} className={`${baseClasses} ${className}`} data-id="element-209">
        {children}
      </a>;
  }
  return <button onClick={onClick} className={`${baseClasses} ${className}`} data-id="element-210">
      {children}
    </button>;
};
export const SectionHeading = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="text-center mb-16" data-id="element-211">
    <h2 className="text-3xl md:text-5xl font-serif text-gold-light mb-6 leading-tight" data-id="element-212">
      {title}
    </h2>
    {subtitle && <div className="w-24 h-1 bg-gold mx-auto rounded-full mb-6" data-id="element-213" />}
    {subtitle && <p className="text-lg md:text-xl text-indigo-100/80 max-w-3xl mx-auto font-light" data-id="element-214">
        {subtitle}
      </p>}
  </div>;
export const StarfieldBackground = () => {
  const isMobile = useIsMobile();
  const [stars, setStars] = useState<{
    id: number;
    x: number;
    y: number;
    size: number;
    duration: number;
    delay: number;
  }[]>([]);
  useEffect(() => {
    // Fewer stars on mobile; rendered as CSS-animated dots (no per-star JS work)
    const count = isMobile ? 18 : 34;
    const generatedStars = Array.from({
      length: count
    }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2
    }));
    setStars(generatedStars);
  }, [isMobile]);
  return <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-215">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-celestial-700/40 via-celestial-900 to-celestial-900" data-id="element-216"></div>

      {stars.map(star => <span key={star.id} className="mp-star absolute rounded-full bg-gold-light" style={{
      left: `${star.x}%`,
      top: `${star.y}%`,
      width: star.size,
      height: star.size,
      animationDuration: `${star.duration}s`,
      animationDelay: `${star.delay}s`
    }} data-id="element-217" />)}
    </div>;
};